<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading :module-id="access.module_id" :profile-id="access.profile_id" :reference="access.view_reference" summary-only />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <AccountingHelp view="accountingPending" />
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }" v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button v-if="selected.length" variant="dark" size="sm" class="mr-1" :disabled="!!progress" @click="generateSelected()"><AppIcon name="playlist_add_check" class="mr-1" />Aprobar seleccionados ({{ selectAll ? table.total : selected.length }})</b-button>
            </div>
          </div>
        </section>
        <AccountingBookBar :businesses="arr.businesses" note="Lo que se aprueba queda en el libro de cada operación: el del punto de venta o el de la caja." @change="onBookChange($event)" />
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Operación</label><b-form-select size="sm" v-model="filters.type" :options="arr.types" @change="load(1)" /></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Desde</label><b-form-input type="date" size="sm" v-model="filters.date_start" @change="load(1)" /></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Hasta</label><b-form-input type="date" size="sm" v-model="filters.date_end" @change="load(1)" /></div>
          </div>
        </b-collapse>
      </b-card>

      <b-alert show variant="warning" v-if="warnings.length">
        <div v-for="(warning, index) in warnings" :key="index">{{ warning }}</div>
      </b-alert>

      <b-card header-tag="header">
        <h6 class="mb-1">Inicio del control contable</h6>
        <small class="text-muted d-block mb-2">Se controla desde el último asiento de apertura. Se carga desde Cierre y Apertura.</small>
        <b-row>
          <b-col md="3" v-for="start in arr.starts" :key="start.business_id + '_' + start.declared" class="account-pending-start" :class="{ 'account-pending-start--current': isCurrentBook(start) }">
            <small class="d-block font-weight-bold"><AccountingBookBadge :declared="start.declared" class="mr-1" />{{ start.business }}</small>
            <span v-if="start.date">Desde {{ moment(start.date).format('DD/MM/YYYY') }}</span>
            <span class="text-danger" v-else>Sin asiento de apertura</span>
          </b-col>
        </b-row>
      </b-card>

      <b-card>
        <b-alert show variant="info" class="py-2" v-if="progress">Aprobando {{ progress.done }} de {{ progress.total }}...</b-alert>
        <div class="account-pending-select-all" v-else-if="allReadySelected && table.total > table.items.length">
          <template v-if="!selectAll">
            Se seleccionaron los listos de esta página.
            <b-button variant="link" size="sm" class="list-view-link p-0" @click="selectAll = true">Seleccionar los {{ table.total }} pendientes del filtro</b-button>
          </template>
          <template v-else>
            Se seleccionaron los {{ table.total }} pendientes del filtro. Los que no estén listos quedan pendientes.
            <b-button variant="link" size="sm" class="list-view-link p-0" @click="selectAll = false">Solo esta página</b-button>
          </template>
        </div>
        <b-table-simple small responsive hover class="app-table mb-0" v-if="table.items.length">
          <b-thead>
            <b-tr>
              <b-th width="3%">
                <b-form-checkbox :checked="allReadySelected" @change="toggleAll($event)" />
              </b-th>
              <b-th width="9%">Fecha</b-th>
              <b-th width="5%">Libro</b-th>
              <b-th width="9%">Operación</b-th>
              <b-th>Comprobante</b-th>
              <b-th width="11%" class="text-right">Importe</b-th>
              <b-th width="34%" class="pl-3">Estado</b-th>
              <b-th width="8%"></b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <template v-for="item in table.items">
            <b-tr :key="item.type + '_' + item.id">
              <b-td><b-form-checkbox v-model="selected" :value="item.type + '_' + item.id" :disabled="!item.ready" /></b-td>
              <b-td>{{ moment(item.date).format('DD/MM/YYYY') }}</b-td>
              <b-td><AccountingBookBadge :declared="item.declared" /></b-td>
              <b-td>{{ item.type_label }}</b-td>
              <b-td class="account-pending-reference" @click="toggleRow(item)">
                <AppIcon :name="isRowOpen(item) ? 'expand_more' : 'chevron_right'" class="mr-1" />{{ item.reference }}
              </b-td>
              <b-td class="text-right">{{ formatCurrency(item.amount_total) }}</b-td>
              <b-td class="pl-3">
                <b-badge variant="success" v-if="item.ready">Listo</b-badge>
                <b-badge variant="warning" v-if="item.imputation">Para imputar</b-badge>
                <small class="text-danger d-block" v-for="(issue, index) in item.issues" :key="index">{{ issue }}</small>
              </b-td>
              <b-td class="text-right">
                <b-button size="sm" variant="outline-secondary" @click="openEditor(item)">{{ item.imputation ? 'Imputar' : 'Ver' }}</b-button>
              </b-td>
            </b-tr>
            <b-tr v-if="isRowOpen(item)" :key="item.type + '_' + item.id + '_lines'">
              <b-td colspan="8" class="account-pending-lines">
                <div v-for="line in item.lines" :key="line.accounting_accounts_id + line.type" class="account-pending-line" :class="{ 'account-pending-line--credit': line.type == 'Haber' }">
                  <span>{{ line.accounting_accounts_code }} {{ line.accounting_accounts_name }}</span>
                  <span>{{ line.type }} {{ formatCurrency(line.total) }}</span>
                </div>
                <div v-if="item.imputation" class="account-pending-line text-warning">
                  <span>{{ item.imputation.label }}</span>
                  <span>{{ item.imputation.type }} {{ formatCurrency(item.imputation.amount) }}</span>
                </div>
                <small class="text-muted" v-if="!item.lines.length && !item.imputation">Sin asiento sugerido.</small>
              </b-td>
            </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
        <EmptyState v-else icon="task_alt" title="Sin movimientos pendientes" description="No hay movimientos sin asiento desde la apertura." />

        <div class="app-table-footer">
          <b-badge v-for="(row, key) in summary" :key="key" variant="light" class="mr-1">{{ row.label }}: {{ row.count }}</b-badge>
          <span class="list-view-toolbar__count">{{ table.total }} pendientes</span>
        </div>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
          <nav v-if="table.total > table.perPage">
            <b-pagination v-if="table.total > table.perPage" class="mb-0" size="sm" pills :total-rows="table.total" :per-page="table.perPage" v-model="table.currentPage" @change="load($event)" />
          </nav>
        </div>
      </b-card>

      <b-modal v-model="modal.editor.active" size="xl" :key="modal.editor._id" :id="modal.editor._id">
        <template #modal-header>
          <div class="app-modal-header">
            <span class="app-modal-header__icon"><AppIcon name="receipt_long" /></span>
            <h5 class="app-modal-header__title">{{ modal.editor.item ? modal.editor.item.reference : '' }}</h5>
          </div>
        </template>

        <div v-if="modal.editor.item">
          <b-alert show variant="warning" v-if="modal.editor.item.issues.length">
            <div v-for="(issue, index) in modal.editor.item.issues" :key="index">{{ issue }}</div>
          </b-alert>
          <b-alert show variant="info" v-if="modal.editor.item.warnings.length">
            <div v-for="(warning, index) in modal.editor.item.warnings" :key="index">{{ warning }}</div>
          </b-alert>
          <b-alert show variant="warning" v-if="modal.editor.item.imputation && !modal.editor.item.imputation.accounts.length">
            {{ modal.editor.item.imputation.label }} por {{ formatCurrency(modal.editor.item.imputation.amount) }}: agregá la cuenta en el {{ modal.editor.item.imputation.type }} del asiento.
          </b-alert>
          <div class="account-pending-split" v-if="modal.editor.item.imputation && modal.editor.item.imputation.accounts.length">
            <h6 class="mb-1">Repartir el gasto de {{ formatCurrency(modal.editor.item.imputation.amount) }}</h6>
            <small class="text-muted d-block mb-2">Indicá cuánto va a cada cuenta del proveedor, según el comprobante. También podés agregar otra cuenta abajo.</small>
            <div class="account-pending-split__row" v-for="row in modal.editor.split" :key="row.accounting_accounts_id + row.alias">
              <span><b>{{ row.alias }}</b> <small class="text-muted">{{ row.accounting_accounts_code }} {{ row.accounting_accounts_name }}</small></span>
              <app-number-input v-model="row.amount" @change="applySplit()" />
            </div>
            <div class="account-pending-split__total" :class="splitRemaining == 0 ? 'text-success' : 'text-warning'">
              {{ splitRemaining == 0 ? 'Gasto imputado completo' : (splitRemaining > 0 ? 'Falta imputar ' : 'Sobran ') + formatCurrency(Math.abs(splitRemaining)) }}
            </div>
          </div>
          <b-form-group label="Libro" class="mb-2" style="max-width:220px;" v-if="!isVoucher(modal.editor.item)">
            <b-form-select size="sm" v-model="modal.editor.declared" :options="arr.books" />
          </b-form-group>
          <AccountingEntryBuilder
            :key="modal.editor.version"
            typeDebe=""
            typeHaber=""
            :defaultDebe="editorLines('Debe')"
            :defaultHaber="editorLines('Haber')"
            :reference="modal.editor.item.reference"
            :date="modal.editor.item.date"
            :declared="modal.editor.declared"
            :amountTotal="0"
            validationMode="manual"
            :showConstructor="true"
            @getAsiento="modal.editor.detail = $event" />
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.editor.active=false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" :disabled="!modal.editor.detail || !!modal.editor.item.blocked" @click="generateEdited()"><AppIcon name="playlist_add_check" class="mr-1" />Aprobar asiento</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>
<script>
  import AccountingHelp from './inc/AccountingHelp'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import StorageLocal from '@/handler/storageLocal'
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import ModuleViewHeading from '@/components/inc/module/ModuleViewHeading'
  import AccountingEntryBuilder from '@/components/inc/accounting/entries'
  import AccountingBookBar from './inc/AccountingBookBar'
  import AccountingBookBadge from './inc/AccountingBookBadge'

  export default {
    components: {
      AccountingBookBar,
      AccountingBookBadge,
      AccountingHelp,
      TabHeaderActions,
      ModuleViewHeading,
      AccountingEntryBuilder,
    },
    data: () => {
      return {
        filtersOpen: false,
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingPending',
          elements: {}
        },
        table: {
          items: [],
          total: 0,
          perPage: 50,
          currentPage: 1,
        },
        summary: {},
        warnings: [],
        selected: [],
        selectAll: false,
        progress: null,
        openRows: {},
        filters: {
          type: null,
          date_start: '',
          date_end: '',
          business_id: null,
          declared: null,
        },
        arr: {
          businesses: [],
          starts: [],
          books: [
            { value: true, text: 'Declarado' },
            { value: false, text: 'No declarado' },
          ],
          types: [
            { value: null, text: 'Todas' },
            { value: 'sales', text: 'Ventas' },
            { value: 'purchase', text: 'Compras' },
            { value: 'receipts', text: 'Recibos' },
            { value: 'payments', text: 'Pagos' },
            { value: 'funds', text: 'Fondos' },
            { value: 'cheque_deposits', text: 'Depósito de cheque' },
            { value: 'cheque_credits', text: 'Acreditación de cheque' },
            { value: 'cheque_rejections', text: 'Cheque rechazado' },
            { value: 'cheque_returns', text: 'Cheque vuelto a cartera' },
            { value: 'cheque_resolutions', text: 'Rechazo resuelto' },
          ],
        },
        modal: {
          editor: {
            active: false,
            item: null,
            detail: null,
            split: [],
            declared: null,
            version: 0,
            _id: '',
          },
        },
      }
    },
    computed: {
      splitRemaining() {
        const item = this.modal.editor.item
        if(!item || !item.imputation) return 0
        const assigned = this.modal.editor.split.reduce((total, row) => total + (parseFloat(row.amount) || 0), 0)
        return Math.round((item.imputation.amount - assigned) * 100) / 100
      },
      readyKeys() {
        return this.table.items.filter(item => item.ready).map(item => item.type + '_' + item.id)
      },
      allReadySelected() {
        return this.readyKeys.length > 0 && this.readyKeys.every(key => this.selected.includes(key))
      },
    },
    created() {
      Helper.hasAccessView(this.access)
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_accounting_pending', value ? 'true' : 'false')
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_accounting_pending') == 'true'
      this._start = true
      this.loadStarts()
      this.loadBusinesses()
    },
    // Con keep-alive, al volver a la pestaña hay que recargar: los movimientos pudieron cambiar
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.loadStarts()
      this.load(1)
    },
    methods: {
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(value)
      },
      // Con empresas, el selector de libro dispara la primera carga
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
        .then(response => {
          this.arr.businesses = (response.data || []).map(business => ({ value: business.id, text: business.name }))
          if(!this.arr.businesses.length) this.load(1)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      onBookChange(book) {
        this.filters.business_id = book.business_id
        this.filters.declared = book.declared
        this.load(1)
      },
      isCurrentBook(start) {
        return start.business_id == this.filters.business_id && (this.filters.declared === null || start.declared === this.filters.declared)
      },
      loadStarts() {
        serviceAPI.obtenerInicioControl()
        .then((response) => {
          this.arr.starts = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      load(page) {
        let loader = this.$loading.show()
        this.selected = []
        this.selectAll = false
        serviceAPI.filtrarAsientosPendientes(this.filters, page)
        .then((response) => {
          loader.hide()
          this.table.items = response.data.data
          this.table.total = response.data.total
          this.table.perPage = response.data.per_page
          this.table.currentPage = response.data.current_page
          this.summary = response.data.summary
          this.warnings = response.data.warnings || []
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      toggleAll(checked) {
        this.selected = checked ? this.readyKeys.slice() : []
        this.selectAll = false
      },
      generate(items) {
        let loader = this.$loading.show()
        return serviceAPI.generarAsientos({ items: items })
        .then((response) => {
          loader.hide()
          const data = response.data
          if(data.generated) {
            this.$awn.success(data.generated + ' asiento/s aprobado/s')
          }
          data.results.filter(result => result.status != 'generated').forEach(result => {
            this.$awn.warning('#' + result.id + ': ' + result.message)
          })
          this.load(this.table.currentPage)
          return data
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      // Todos los del filtro, por tandas: el backend omite los que no están listos.
      generateAll() {
        const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: 'playlist_add_check' } }),
          'Aprobar asientos'
        ])
        return this.$bvModal.msgBoxConfirm('Se aprueban los asientos listos de los ' + this.table.total + ' pendientes del filtro. Los demás quedan para revisar.', {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'dark',
          okTitle: 'Aprobar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerClass: 'app-confirm-header',
          centered: true
        })
        .then(value => {
          if(!value) return null
          return serviceAPI.obtenerClavesPendientes(this.filters).then(response => this.approveBatches(response.data))
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.progress = null
          this.load(1)
        })
      },
      async approveBatches(keys) {
        const size = 100
        let approved = 0
        this.progress = { done: 0, total: keys.length }
        for(let index = 0; index < keys.length; index += size) {
          const response = await serviceAPI.generarAsientos({ items: keys.slice(index, index + size) })
          approved += response.data.generated
          this.progress.done = Math.min(index + size, keys.length)
        }
        if(approved) {
          this.$awn.success(approved + ' asiento/s aprobado/s')
        }
        if(keys.length > approved) {
          this.$awn.warning((keys.length - approved) + ' comprobante/s quedaron pendientes para revisar')
        }
      },
      generateSelected() {
        if(this.selectAll) {
          this.generateAll()
          return
        }
        this.generate(this.selected.map(key => {
          const index = key.lastIndexOf('_')
          return { type: key.slice(0, index), id: parseInt(key.slice(index + 1)) }
        }))
      },
      openEditor(item) {
        this.modal.editor.item = item
        this.modal.editor.detail = null
        this.modal.editor.declared = item.declared
        this.modal.editor.split = item.imputation
          ? item.imputation.accounts.map(account => Object.assign({ amount: 0 }, account))
          : []
        this.modal.editor._id = Math.random().toString(36).slice(2, 8)
        this.modal.editor.active = true
      },
      editorLines(type) {
        const item = this.modal.editor.item
        const lines = item.lines.filter(line => line.type == type)
        const split = item.imputation && item.imputation.type == type
          ? this.modal.editor.split.filter(row => parseFloat(row.amount) > 0).map(row => Object.assign({ total: parseFloat(row.amount) }, row))
          : []
        return lines.concat(split).map(line => ({
          account: { id: line.accounting_accounts_id, code: line.accounting_accounts_code, name: line.accounting_accounts_name },
          amount: line.total,
        }))
      },
      applySplit() {
        this.modal.editor.version++
      },
      isRowOpen(item) {
        return !!this.openRows[item.type + '_' + item.id]
      },
      toggleRow(item) {
        const key = item.type + '_' + item.id
        this.$set(this.openRows, key, !this.openRows[key])
      },
      isVoucher(item) {
        return ['sales', 'purchase', 'receipts', 'payments'].includes(item.type)
      },
      generateEdited() {
        const item = this.modal.editor.item
        this.generate([{ type: item.type, id: item.id, detail: this.modal.editor.detail, declared: this.modal.editor.declared }])
        .then((data) => {
          if(data && data.generated) {
            this.modal.editor.active = false
          }
        })
      },
    }
  }
</script>
<style scoped>
  .account-pending-start {
    padding-top: 6px;
    padding-bottom: 6px;
    border-radius: 6px;
    opacity: 0.6;
  }
  .account-pending-start--current {
    opacity: 1;
    background: var(--app-color-surface-subtle);
  }
  .account-pending-select-all {
    margin-bottom: 8px;
    padding: 6px 10px;
    font-size: 13px;
    text-align: center;
    background: var(--app-color-surface-subtle);
    border-radius: 6px;
  }
  .account-pending-reference {
    cursor: pointer;
  }
  .account-pending-lines {
    padding: 6px 12px 10px 36px;
    background: var(--app-color-surface-subtle);
  }
  .account-pending-line {
    display: flex;
    justify-content: space-between;
    max-width: 640px;
    font-size: 12px;
    line-height: 20px;
  }
  .account-pending-line--credit {
    padding-left: 24px;
  }
  .account-pending-split {
    margin-bottom: 12px;
    padding: 10px 12px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
  }
  .account-pending-split__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
  }
  .account-pending-split__row > :last-child {
    flex: 0 0 180px;
  }
  .account-pending-split__total {
    margin-top: 6px;
    text-align: right;
    font-weight: 600;
  }
</style>
